/**
 * Cum se spune pe ecran unde a ajuns un colet.
 *
 * Regula trăia într-un singur loc — ecranul de expediții — iar de pe 01.10.2026 e nevoie de ea și
 * pe cererea de marfă. Scrisă a doua oară, ar fi divergat: două ecrane ar fi spus lucruri diferite
 * despre același colet, iar cine le-ar fi văzut pe amândouă n-ar fi știut pe care să creadă.
 *
 * ORDINEA CONTEAZĂ și e aceeași ca în brief: finalul bate blocajul, blocajul bate întârzierea.
 * O expediție blocată e și întârziată, dar „blocată" spune mai mult — nu s-a mișcat deloc.
 */

export interface ExpeditiePeEcran {
  lastEventName?: string | null
  deliveredAt?: string | Date | null
  returnAwbNumber?: string | null
  closedAt?: string | Date | null
  inReturn?: boolean | null
  blockedSince?: string | Date | null
  /** Zile lucratoare la curier, peste pragul unic de intarziere (4); altfel null. */
  intarziere?: number | null
  /**
   * Starea comuna din API (`stareColet.ts`), din 12.10.2026: nepreluat | in_tranzit | in_retur |
   * refuzat | livrat | retur | inchis | anulat | inlocuit | neurmarit. Lipsa = rand de dinainte, se deduce
   * din campuri.
   */
  stare?: string | null
}

export type FelStare = 'livrata' | 'retur' | 'blocata' | 'intarziata' | 'in_curs'

export interface StarePeEcran {
  fel: FelStare
  text: string
  /** Clasa de culoare, ca ecranele să arate la fel fără să repete fiecare paleta. */
  clasa: string
}

const zi = (d: string | Date) => new Date(d).toLocaleDateString('ro-RO')

/** Câte zile întregi au trecut. Fără ore: pe ecran „de 0 zile" n-ar însemna nimic. */
function zileDe(d: string | Date): number {
  return Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000))
}

export function stareExpeditie(e: ExpeditiePeEcran): StarePeEcran {
  if (e.deliveredAt) {
    return { fel: 'livrata', text: `Livrat ${zi(e.deliveredAt)}`, clasa: 'text-emerald-700' }
  }
  // Inchis de mana nu e retur: coletul n-a plecat, sau s-a rezolvat altfel (vezi motivul).
  if (e.stare === 'inchis') {
    return { fel: 'retur', text: 'Închis manual', clasa: 'text-b4l-text-muted' }
  }
  if (e.stare === 'livrat') {
    return { fel: 'livrata', text: 'Livrat', clasa: 'text-emerald-700' }
  }
  // Inainte de „Retur": un AWB anulat are si el `closedAt`, dar n-a plecat nicaieri.
  if (e.stare === 'anulat') {
    return { fel: 'retur', text: 'AWB anulat', clasa: 'text-b4l-text-muted' }
  }
  // Plecat pe alt AWB (redirectionat, reemis), 21.10.2026. Tot cu `closedAt`, deci inainte de „Retur".
  if (e.stare === 'inlocuit') {
    return { fel: 'retur', text: 'Înlocuit de alt AWB', clasa: 'text-b4l-text-muted' }
  }
  // FAN nu mai e intrebat dupa 60 de zile: nu stim unde e, si nu pretindem ca stim.
  if (e.stare === 'neurmarit') {
    return { fel: 'retur', text: 'Fără urmărire (60+ zile)', clasa: 'text-b4l-text-muted' }
  }
  if (e.returnAwbNumber || e.closedAt || e.stare === 'retur') {
    return { fel: 'retur', text: 'Retur', clasa: 'text-b4l-text-muted' }
  }
  if (e.blockedSince) {
    /* „Nepreluată" când curierul n-a raportat nicio mișcare; „blocată" când s-a oprit pe drum. */
    const cap = e.lastEventName ? 'Blocat' : 'Nepreluat'
    return {
      fel: 'blocata',
      text: `${cap} de ${zileDe(e.blockedSince)} zile`,
      clasa: 'text-b4l-red',
    }
  }
  if (e.intarziere) {
    return {
      fel: 'intarziata',
      text: `Întârziat de ${e.intarziere} zile lucrătoare`,
      clasa: 'text-b4l-red',
    }
  }
  if (e.inReturn || e.stare === 'in_retur') {
    return { fel: 'retur', text: 'Se întoarce', clasa: 'text-b4l-text-muted' }
  }
  if (e.stare === 'refuzat') {
    return { fel: 'in_curs', text: 'Refuzat', clasa: 'text-b4l-amber-dark' }
  }
  if (e.stare === 'nepreluat') {
    return { fel: 'in_curs', text: 'Nepreluat', clasa: 'text-b4l-text-muted' }
  }
  return { fel: 'in_curs', text: 'În tranzit', clasa: 'text-b4l-text-muted' }
}
